'use client'

import { useEffect, useRef, useState } from 'react'

import {
  FALLBACK_HEADER_BOTTOM_PX,
  NAVIGATION_SETTLE_MS,
  SECTION_NAV_HEIGHT_PX,
  SECTION_NAV_ITEMS,
} from '@/app/components/sections/performance/PerformanceSectionNav/constants'
import type { SectionNavItem } from '@/app/components/sections/performance/PerformanceSectionNav/types'

export interface UseSectionNavOptions {
  readonly shareBarId: string
  readonly heroId: string
}

export interface UseSectionNavResult {
  /** Destinos que existen realmente en esta ficha, en orden de página. */
  readonly items: readonly SectionNavItem[]
  /** La barra debe estar en pantalla. */
  readonly visible: boolean
  /** `id` de la sección marcada como actual. */
  readonly activeId: string | undefined
  /** Marca la pestaña pulsada y lleva el scroll a su sección. */
  readonly handleSelect: (id: string) => void
}

/** Borde inferior del hueco visible: lo que tapa el `<header>` sticky. */
function getHeaderBottom(): number {
  const bottom = document.querySelector('header')?.getBoundingClientRect().bottom
  return typeof bottom === 'number' && !Number.isNaN(bottom) ? bottom : FALLBACK_HEADER_BOTTOM_PX
}

/**
 * `useSectionNav` — visibilidad y sección activa de la barra de secciones de la
 * PDP.
 *
 * Tres responsabilidades, resueltas en una sola medición por frame:
 *
 * 1. **Qué pestañas hay.** `SECTION_NAV_ITEMS` es la lista de candidatos; las
 *    secciones de la ficha se omiten cuando no hay dato, así que se filtran por
 *    presencia en el DOM. Se comprueba una vez tras el montaje: las secciones
 *    son HTML de servidor, no aparecen ni desaparecen después.
 * 2. **Cuándo se muestra.** Cuando la banda de compartición del hero ha salido
 *    por arriba, es decir cuando su borde inferior ya está por encima del borde
 *    inferior del header sticky (que tapa contenido aunque este siga
 *    intersectando el viewport). Mismo criterio de «hueco realmente visible»
 *    que `useBuyBoxVisibility`. Para retirarla el umbral es el borde inferior
 *    de la barra desplegada, no el del header: ver la histéresis en `measure`.
 * 3. **Qué pestaña está activa.** Gana la última sección cuyo borde superior ha
 *    pasado ya por debajo de la propia barra.
 *
 * Se escucha `scroll`/`resize` agrupados en `requestAnimationFrame` y **no** se
 * usa `IntersectionObserver`: el cruce solo dispara al entrar y al salir y el
 * valor se queda pegado en escenarios de rebote — que aquí son la norma, porque
 * el usuario sube y baja por los mismos bordes. Es el mismo razonamiento ya
 * documentado en `Header.tsx`, `useBuyBoxVisibility` y `useActiveLetter`.
 */
export function useSectionNav({ shareBarId, heroId }: UseSectionNavOptions): UseSectionNavResult {
  const [items, setItems] = useState<readonly SectionNavItem[]>([])
  const [visible, setVisible] = useState(false)
  const [activeId, setActiveId] = useState<string | undefined>(undefined)

  // Mientras es `true`, el scroll-spy no recalcula por posición: la sección
  // activa ya se fijó de forma determinista al pulsar (ver `handleSelect`).
  const isNavigatingRef = useRef(false)
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    let frame = 0

    // Las secciones de la ficha son HTML de servidor: se descubren una vez, al
    // montar, y no aparecen ni desaparecen después.
    const present = SECTION_NAV_ITEMS.filter((item) => document.getElementById(item.id) !== null)

    const measure = () => {
      frame = 0
      // Misma referencia en cada medición: React descarta el resto de
      // actualizaciones, así que esto solo provoca un render (el primero).
      setItems(present)

      const headerBottom = getHeaderBottom()
      // Borde inferior de la banda que tapa la página: header + la propia barra
      // cuando está desplegada. Por encima de aquí no hay nada que el usuario
      // pueda ver.
      const threshold = headerBottom + SECTION_NAV_HEIGHT_PX

      // Sin ancla de referencia (la banda de compartición es condicional) se
      // usa el hero: si tampoco estuviera, la barra no llegaría a mostrarse.
      const trigger = document.getElementById(shareBarId) ?? document.getElementById(heroId)
      const triggerBottom = trigger?.getBoundingClientRect().bottom
      // Histéresis, y no un único umbral: la barra aparece cuando el ancla se
      // esconde tras el header, pero solo se retira cuando el ancla vuelve a
      // asomar por debajo de la propia barra. Con un umbral único la barra se
      // cerraba al pulsar su primera pestaña: el salto deja el borde superior
      // de esa sección justo bajo la barra (`pdp-section-anchor`) y la banda de
      // compartición cierra el hero pegada a esa sección, así que reaparecía
      // dentro de los 48px que la barra tapa y la medición la leía como
      // «visible otra vez».
      //
      // Ese aterrizaje cae *exactamente* en el umbral, así que además no se
      // retira mientras el scroll del clic está en marcha: durante esa ventana
      // la visibilidad solo puede encenderse, nunca apagarse, y medio píxel de
      // redondeo del scroll suave no puede volver a cerrar la barra.
      setVisible((previous) => {
        if (triggerBottom === undefined) return false
        if (triggerBottom <= headerBottom) return true
        if (isNavigatingRef.current) return previous
        return previous && triggerBottom <= threshold
      })

      // El viewport está «de camino» al destino tras un clic: recalcular aquí
      // haría parpadear las secciones intermedias que se cruzan.
      if (isNavigatingRef.current) return

      let current = present[0]?.id
      for (const item of present) {
        const section = document.getElementById(item.id)
        if (section && section.getBoundingClientRect().top <= threshold) {
          current = item.id
        }
      }
      setActiveId((previous) => (previous === current ? previous : current))
    }

    // `scroll` puede dispararse muchas veces por frame y `measure` hace varias
    // lecturas de layout: se agrupan en un único `requestAnimationFrame`.
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [shareBarId, heroId])

  useEffect(() => {
    return () => {
      if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current)
    }
  }, [])

  function handleSelect(id: string) {
    // Se marca activa de inmediato (determinista: es la pulsada) en vez de
    // esperar a que el scroll-spy la infiera de la posición, y se suspende el
    // spy durante `NAVIGATION_SETTLE_MS` para que no la pise con una sección
    // intermedia mientras el scroll suave todavía está en marcha.
    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current)
    isNavigatingRef.current = true
    settleTimeoutRef.current = setTimeout(() => {
      isNavigatingRef.current = false
    }, NAVIGATION_SETTLE_MS)

    setActiveId(id)

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    document.getElementById(id)?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    })
  }

  return { items, visible, activeId, handleSelect }
}
